import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Homepage integration block — "EnergiPro Group"
 *  Drop this into Home.tsx directly ABOVE the Clients & Partners
 *  section. Uses only existing site utilities.
 * ------------------------------------------------------------------ */

const companies = [
  {
    name: 'EnergiPro Solutions',
    country: 'Saudi Arabia',
    scope:
      'Surface well testing, pressure control, stimulation & nitrogen, oilfield chemicals, equipment rental and field services.',
    to: '/services',
    cta: 'View Capabilities',
    current: true,
  },
  {
    name: 'KIMKO',
    country: 'United Arab Emirates',
    scope:
      'MEP engineering, HVAC design, fire protection, low current systems, authority approvals and construction support.',
    to: '/companies/kimko',
    cta: 'Visit KIMKO',
    current: false,
  },
  {
    name: 'Petronix Energy Services',
    country: 'Egypt',
    scope:
      'Equipment rental, industrial supply, technical manpower, maintenance, shutdown & commissioning support and field logistics.',
    to: '/companies/petronix',
    cta: 'Visit Petronix',
    current: false,
  },
];


function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

export default function GroupCompaniesHome() {
  const { ref, visible } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`py-20 md:py-28 bg-slate-950 text-white reveal-stagger ${visible ? 'is-visible' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">
            EnergiPro Group
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            Three Companies. Three Markets. One Standard.
          </h2>
          <div className="h-1 w-16 bg-orange-600 mt-4 mx-auto" />
          <p className="mt-6 text-slate-300 leading-relaxed">
            EnergiPro Group operates specialist companies in Saudi Arabia, the United Arab Emirates
            and Egypt — covering oilfield services, building services engineering and industrial
            support under shared governance and a single commercial interface.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {companies.map((c) => (
            <Link
              key={c.name}
              to={c.to}
              className={`group flex flex-col border p-8 transition-all duration-300 hover:-translate-y-1 ${
                c.current
                  ? 'border-orange-600 bg-orange-600/10'
                  : 'border-white/15 bg-white/5 hover:border-orange-600/60'
              }`}
            >
              <div className="flex items-center gap-3 mb-5">
                <Building2 className="w-5 h-5 text-orange-500" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  {c.country}
                </span>
              </div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-white mb-3">
                {c.name}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed flex-1">{c.scope}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-orange-500">
                {c.cta}
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
